/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from "react";
import { useTheme } from "../../theme/ThemeContext";
import Button from "../Button";

/**
 * ContentDisplayModal
 * A generic, theme-aware modal for displaying content (e.g., logs, details).
 * 
 * Props:
 * - open (boolean): whether to show the modal
 * - title (string): modal title
 * - onClose (function): callback to close modal
 * - children: content to display
 * - width, maxWidth, maxHeight: layout control
 */
export default function ContentDisplayModal({
    open,
    title,
    onClose,
    children,
    width = "90vw",
    maxWidth = 800,
    maxHeight = "80vh",
    zIndex = 10000
}) {
    const { theme } = useTheme();

    if (!open) return null;

    const overlayStyle = {
        position: "fixed",
        inset: 0,
        zIndex: zIndex,
        background: "rgba(30,40,60,0.25)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
    };

    const containerStyle = {
        width: width,
        maxWidth: maxWidth,
        maxHeight: maxHeight,
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        borderRadius: theme.cardRadius,
        boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
        overflow: "hidden",
        border: `1px solid ${theme.primary}20` // slight border transparency
    };

    const headerStyle = {
        padding: "16px 24px",
        background: "#fafbfc",
        borderBottom: `1px solid ${theme.primary}20`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexShrink: 0
    };

    const titleStyle = {
        fontWeight: 700,
        fontSize: 18,
        color: theme.primary
    };

    const bodyStyle = {
        padding: 24,
        overflowY: "auto",
        wordBreak: "break-word",
        flexGrow: 1,
        fontSize: 14,
        color: "#333",
        lineHeight: 1.6
    };

    const footerStyle = {
        padding: "16px 24px",
        borderTop: `1px solid ${theme.primary}10`,
        background: "#fff",
        display: "flex",
        justifyContent: "flex-end",
        flexShrink: 0
    };

    return (
        <div
            style={overlayStyle}
            role="dialog"
            aria-modal="true"
        >
            <div style={containerStyle}>
                <div style={headerStyle}>
                    <div style={titleStyle}>{title}</div>
                    <button
                        onClick={onClose}
                        style={{
                            background: "transparent",
                            border: "none",
                            fontSize: 24,
                            lineHeight: 1,
                            color: "#999",
                            cursor: "pointer",
                            padding: 0
                        }}
                        aria-label="Close"
                    >
                        &times;
                    </button>
                </div>

                <div style={bodyStyle}>
                    {children}
                </div>

                <div style={footerStyle}>
                    <Button mode="primary-bold" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>
        </div>
    );
}
